import React from 'react';

import {NetworkStatus} from '@jimmyn/apollo-client';
import {LinkOutlined} from '@ant-design/icons';
import {Button, Divider, Skeleton, Typography} from 'antd';
import styled from 'styled-components';

import {useCreateShortLinkMutation, useShortLinkQuery} from 'api/generated';
import {shortLinkQuery} from 'api/queries';
import {AnchorLink} from 'components';
import {config} from 'stage.config';

const {Text} = Typography;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LinksGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface Props {
  url: string;
  accountId: string;
  label: React.ReactNode;
  linkLabel?: React.ReactNode;
  expireAt?: number;
}

export const ShortLinkField: React.FC<Props> = ({url, accountId, label, linkLabel, expireAt}) => {
  const {data, networkStatus} = useShortLinkQuery({
    variables: {url, accountId},
    skip: !url || !accountId,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    context: {useApolloNetworkStatus: false}
  });
  const ready =
    networkStatus === NetworkStatus.ready ||
    networkStatus === NetworkStatus.error ||
    !url ||
    !accountId;

  const [createShortLink, {loading: creating}] = useCreateShortLinkMutation();

  const shortLink = data?.shortLink;
  const shortLinkUrl = shortLink ? `https://${config.shortLinkDomain}/${shortLink.id}` : '';
  const displayUrl = shortLinkUrl || url;

  const handleCreateShortLink = async () => {
    await createShortLink({
      variables: {input: {url, accountId, expireAt}},
      update: (cache, {data: result}) => {
        if (result?.createShortLink) {
          cache.writeQuery({
            query: shortLinkQuery,
            variables: {url, accountId},
            data: {shortLink: result.createShortLink}
          });
        }
      }
    });
  };

  if (!ready) {
    return (
      <Container>
        {label}
        <Skeleton.Input active size="small" style={{width: 150, height: 22}} />
      </Container>
    );
  }

  return (
    <Container>
      {label}
      <LinksGroup>
        <Text copyable={{text: displayUrl}} style={{fontWeight: 'normal'}}>
          <AnchorLink href={displayUrl} external target="_blank">
            {shortLink ? shortLinkUrl : linkLabel || 'Permalink'}
          </AnchorLink>
        </Text>
        {!shortLink && (
          <>
            <Divider type="vertical" />
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              loading={creating}
              onClick={handleCreateShortLink}
              style={{padding: 0}}>
              <span>Get short link</span>
            </Button>
          </>
        )}
      </LinksGroup>
    </Container>
  );
};
